import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserRound, Compass, FileCheck2, LineChart, ShieldCheck, Target, BadgeCheck, Map, Gift, Eye, Lock, Building2, Cpu, Trash2, Scale, ArrowRight, Link2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { StartCta } from "./SiteChrome";

const STEPS = [
  { icon: UserRound, title: "Tell Us About Yourself", desc: "Upload your CV or build your career profile." },
  { icon: Compass, title: "Understand Your Opportunities", desc: "Explore job compatibility, experience gaps and career pathways." },
  { icon: FileCheck2, title: "Put Your Best Application Forward", desc: "Generate tailored CVs and cover letters using verified career information." },
  { icon: LineChart, title: "Prepare, Apply and Improve", desc: "Track applications, practise interviews and learn from actual outcomes." },
];

const Heading = ({ eyebrow, title, sub }) => (
  <div className="max-w-2xl mb-12">
    <div className="gi-eyebrow mb-3">{eyebrow}</div>
    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-ink">{title}</h2>
    {sub && <p className="mt-4 text-lg text-body">{sub}</p>}
  </div>
);

export const HowItWorks = () => (
  <section id="how" data-testid="how-it-works-section" className="scroll-mt-20 bg-surface border-y border-line">
    <div className="max-w-7xl mx-auto px-5 sm:px-6 py-20 sm:py-28">
      <Heading eyebrow="How GetInterviewed works" title="Four steps from your CV to your next interview" />
      <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {STEPS.map((s, i) => (
          <li key={s.title} data-testid={`step-card-${i + 1}`} className="group rounded-2xl border border-line bg-canvas p-6 hover:border-brand/40 hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
            <div className="flex items-center justify-between mb-6">
              <div className="w-11 h-11 rounded-xl bg-brand-soft flex items-center justify-center">
                <s.icon className="w-5 h-5 text-brand" aria-hidden="true" />
              </div>
              <span className="w-7 h-7 rounded-full bg-navy text-white text-xs font-bold flex items-center justify-center" aria-label={`Step ${i + 1}`}>{i + 1}</span>
            </div>
            <h3 className="font-display font-semibold text-lg text-ink mb-2">{s.title}</h3>
            <p className="text-[15px] text-body leading-relaxed">{s.desc}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const WHY = [
  { icon: ShieldCheck, title: "Your history stays under your control", desc: "You decide what goes on your CV. Nothing is added without your confirmation, and you can delete your records." },
  { icon: Target, title: "Tailored to each opportunity", desc: "Every CV and cover letter is shaped around the specific job you're applying for, not a one-size-fits-all template." },
  { icon: BadgeCheck, title: "Grounded in evidence", desc: "Recommendations come from your real CV and your real application results, and we show why." },
  { icon: Map, title: "Guidance beyond the CV", desc: "Fit checks, gap coaching, interview practice and a guided Career Journey for your next move." },
  { icon: Gift, title: "Free tools, honest promises", desc: "The core tools are free. We never promise interviews or jobs we can't guarantee." },
  { icon: Eye, title: "Transparent about your data", desc: "Plain-English explanations of what we store, how AI processes it, and how to remove it." },
];

export const WhySection = () => (
  <section id="why" data-testid="why-section" className="scroll-mt-20 bg-surface border-y border-line">
    <div className="max-w-7xl mx-auto px-5 sm:px-6 py-20 sm:py-28">
      <Heading eyebrow="Why GetInterviewed?" title="A career companion, not just a CV generator" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
        {WHY.map((w, i) => (
          <div key={w.title} data-testid={`why-item-${i}`} className="flex gap-4">
            <div className="w-11 h-11 rounded-xl bg-brand-soft flex items-center justify-center shrink-0"><w.icon className="w-5 h-5 text-brand" aria-hidden="true" /></div>
            <div>
              <h3 className="font-display font-semibold text-lg text-ink">{w.title}</h3>
              <p className="text-[15px] text-body leading-relaxed mt-1.5">{w.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const TRUST = [
  { id: "data-protection", icon: Lock, title: "Protected and private", desc: "Everything travels over encrypted HTTPS. Your CVs are stored privately and can only be opened when you're signed in. There are no public file links." },
  { id: "employer-sharing", icon: Building2, title: "Never shared with employers", desc: "We don't send your information to employers or recruiters. It only leaves GetInterviewed when you choose to email an application yourself." },
  { id: "ai-processing", icon: Cpu, title: "How AI reads your documents", desc: "To tailor your CV, its text and the job description are sent to our AI providers (OpenAI, Anthropic or Google) to produce results. We don't sell your data or use it to train our own models." },
  { id: "data-deletion", icon: Trash2, title: "Deleting your information", desc: "Delete any application from your tracker, or go to Privacy & Data in your dashboard to download everything we hold, delete your CV, or erase your whole account yourself." },
  { id: "career-boundaries", icon: Scale, title: "Guidance, not guarantees", desc: "We help you present your genuine experience well. We can't promise interviews or jobs, and our tips aren't legal, immigration or financial advice." },
];

const LINKS = [["/privacy", "Privacy Policy"], ["/terms", "Terms of Service"], ["/data-retention", "Data Retention Policy"], ["/pdpa", "PDPA Statement"], ["/data-retention#deleting", "Account & data deletion"], ["mailto:privacy@getinterviewed.app", "Contact & support"]];

export const TrustSection = () => (
  <section id="trust" data-testid="trust-privacy-section" className="scroll-mt-20 bg-surface border-t border-line">
    <div className="max-w-7xl mx-auto px-5 sm:px-6 py-20 sm:py-28">
      <Heading eyebrow="Trust & privacy" title="Your career history is personal. We treat it that way." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {TRUST.map((t) => (
          <div key={t.id} data-testid={`privacy-card-${t.id}`} className="rounded-2xl border border-line bg-canvas p-6">
            <t.icon className="w-5 h-5 text-kesh mb-4" aria-hidden="true" />
            <h3 className="font-display font-semibold text-lg text-ink mb-2">{t.title}</h3>
            <p className="text-[15px] text-body leading-relaxed">{t.desc}</p>
          </div>
        ))}
        <div className="rounded-2xl border border-brand/20 bg-brand-soft p-6" data-testid="trust-links">
          <h3 className="font-display font-semibold text-lg text-ink mb-3">Read the details</h3>
          <ul className="space-y-2.5">
            {LINKS.map(([h, l]) => (
              <li key={l}><a href={h} className="text-[15px] font-medium text-brand-text hover:underline inline-flex items-center gap-1.5">{l} <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" /></a></li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

export const FinalCta = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [v, setV] = useState("");
  const go = () => {
    const t = v.trim();
    if (t) localStorage.setItem(/^https?:\/\//i.test(t) ? "pending_url" : "pending_jd", t);
    navigate(user ? "/dashboard" : "/signin");
  };
  return (
    <section className="bg-navy text-white" data-testid="final-cta-section">
      <div className="max-w-5xl mx-auto px-5 sm:px-6 py-20 sm:py-28 text-center">
        <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight text-white">Your experience matters.<br className="hidden sm:block" /> Make sure employers can see it.</h2>
        <StartCta testid="final-cta-button" className="mt-9">Start Your Career Journey</StartCta>
        <div className="mt-12 max-w-2xl mx-auto text-left">
          <label htmlFor="hero-jd-input" className="block text-[15px] text-white/80 mb-2">Already have a job in mind? Paste the link or description and we'll take you straight to tailoring.</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Link2 className="w-4 h-4 text-faint absolute left-4 top-1/2 -translate-y-1/2" aria-hidden="true" />
              <input id="hero-jd-input" data-testid="hero-jd-input" value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && go()}
                placeholder="https://company.com/jobs/123 or paste the job description"
                className="gi-input pl-11 min-h-[50px] bg-white text-[#14243B] placeholder:text-[#5B6779] border-white" />
            </div>
            <button data-testid="hero-start-button" onClick={go} className="gi-btn gi-btn-lg bg-white text-[#14243B] hover:bg-white/90 shrink-0">
              Tailor my CV <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
